import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import {
  ChoiceDialog,
  SelectDialogProps,
} from '@rossum/components/choice-dialog/ChoiceDialog';
import { useQueue } from '../hooks/useQueue';
import { useWorkspacesWithQueues } from '../hooks/useWorkspacesWithQueues';
import { QueueSelectList } from './QueueSelectList';

type QueueDialogProps = Omit<
  SelectDialogProps,
  'onConfirm' | 'submitText' | 'cancelText' | 'children'
> & {
  onConfirm: (queue: Queue) => void;
  submitText?: SelectDialogProps['submitText'];
  cancelText?: SelectDialogProps['cancelText'];
};

/**
 * Controller variant of Select queue dialog with pre-filled defaults.
 */
export const QueueDialog = ({
  onConfirm,
  submitText,
  cancelText,
  isDisabled,
  ...props
}: QueueDialogProps) => {
  const intl = useIntl();
  const [selectedQueueId, setSelectedQueueId] = useState<number | undefined>();

  const { workspacesWithQueues: queuesByWorkspace, isLoading } =
    useWorkspacesWithQueues({
      enableQueries: true,
    });

  const { data: selectedQueue } = useQueue(selectedQueueId);

  return (
    <ChoiceDialog
      submitText={
        submitText ??
        intl.formatMessage({
          id: 'components.selectQueueDialog.submit',
        })
      }
      cancelText={
        cancelText ??
        intl.formatMessage({
          id: 'components.selectQueueModal.cancel',
        })
      }
      isDisabled={isDisabled || selectedQueueId === undefined}
      {...props}
      onConfirm={() => {
        if (selectedQueue) {
          onConfirm(selectedQueue);
        }
      }}
    >
      {props.open && (
        <QueueSelectList
          multiSelect={false}
          selectedQueues={selectedQueueId ? [selectedQueueId] : []}
          onSelect={queues => setSelectedQueueId(queues[0])}
          queuesByWorkspace={queuesByWorkspace ?? []}
          isLoading={isLoading}
        />
      )}
    </ChoiceDialog>
  );
};
